import React, { useState, useEffect } from 'react';
import { AdminWalletAPI, AdminAPIUtils, AdminTokenService } from '../services/api';
import { 
  Bitcoin, Copy, CheckCircle, AlertCircle, RefreshCw,
  Shield, Eye, EyeOff, Save, ExternalLink
} from 'lucide-react';

const WalletSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [depositAddress, setDepositAddress] = useState('');
  const [originalAddress, setOriginalAddress] = useState('');
  const [message, setMessage] = useState(null);
  const [showAddress, setShowAddress] = useState(true);

  useEffect(() => {
    fetchDepositAddress();
  }, []);

  const fetchDepositAddress = async () => {
    try {
      setLoading(true);
      const response = await AdminWalletAPI.getSystemDepositAddress();
      console.log('Deposit address response:', response); // Debug log
      
      if (response && response.success !== false) {
        setDepositAddress(response.address || '');
        setOriginalAddress(response.address || '');
        setMessage(null);
      } else {
        setMessage({ 
          type: 'error', 
          text: response?.error || 'Failed to load deposit address' 
        });
      }
    } catch (err) {
      console.error('Failed to fetch deposit address:', err);
      setMessage({ 
        type: 'error', 
        text: err?.message || 'Failed to load deposit address. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!depositAddress.trim()) {
      setMessage({ type: 'error', text: 'Please enter a valid Bitcoin address' });
      return;
    }

    try {
      setSaving(true);
      const response = await AdminWalletAPI.updateSystemDepositAddress(depositAddress.trim());
      console.log('Update response:', response); // Debug log
      
      if (response && response.success !== false) {
        setOriginalAddress(depositAddress.trim());
        setMessage({ type: 'success', text: 'Deposit address updated successfully!' });
        setTimeout(() => setMessage(null), 5000);
      } else {
        setMessage({ 
          type: 'error', 
          text: response?.error || 'Failed to update deposit address' 
        });
      }
    } catch (err) {
      console.error('Failed to update deposit address:', err);
      setMessage({ 
        type: 'error', 
        text: err?.message || 'Failed to update deposit address. Please try again.' 
      });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    alert('Address copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
          <p className="text-charcoal-gray">Loading wallet settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            ⚙️ Wallet Settings
          </h1>
          <p className="text-charcoal-gray mt-1">Configure system wallet settings and deposit addresses</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchDepositAddress}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Settings Card */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
          <Bitcoin className="w-5 h-5 text-yellow-400" />
          System Deposit Address
        </h2>

        <p className="text-charcoal-gray text-sm mb-6">
          This is the Bitcoin address where users will send their deposits. 
          All deposit proofs will reference this address.
        </p>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        {/* Address Input */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Bitcoin Address
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <Bitcoin className="w-5 h-5 text-yellow-400" />
              </div>
              <input
                type={showAddress ? 'text' : 'password'}
                value={depositAddress}
                onChange={(e) => setDepositAddress(e.target.value)}
                placeholder="1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                className="w-full pl-12 pr-20 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors font-mono"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                <button
                  onClick={() => setShowAddress(!showAddress)}
                  className="p-2 text-charcoal-gray hover:text-white transition-colors rounded-lg hover:bg-gray-800"
                  title={showAddress ? 'Hide Address' : 'Show Address'}
                >
                  {showAddress ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {depositAddress && (
                  <button
                    onClick={() => copyToClipboard(depositAddress)}
                    className="p-2 text-charcoal-gray hover:text-white transition-colors rounded-lg hover:bg-gray-800"
                    title="Copy Address"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-charcoal-gray text-xs mt-2">
              Enter a valid Bitcoin address where users should send their deposits
            </p>
          </div>

          {/* Current Address Display */}
          {originalAddress && (
            <div className="bg-gray-800/30 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-gray-300">Current Address:</span>
                  <code className="text-sm font-mono text-yellow-400 bg-black/30 px-3 py-1 rounded-lg">
                    {originalAddress}
                  </code>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => copyToClipboard(originalAddress)}
                    className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    title="Copy Address"
                  >
                    <Copy className="w-3 h-3 text-white" />
                  </button>
                  <a
                    href={`https://www.blockchain.com/explorer/addresses/btc/${originalAddress}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                    title="View on Explorer"
                  >
                    <ExternalLink className="w-3 h-3 text-white" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-800">
            <button
              onClick={() => {
                setDepositAddress(originalAddress);
                setMessage(null);
              }}
              disabled={depositAddress === originalAddress}
              className={`px-5 py-2.5 rounded-xl transition-all duration-300 ${
                depositAddress === originalAddress
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-[1.02]'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || depositAddress === originalAddress || !depositAddress.trim()}
              className={`px-5 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                saving || depositAddress === originalAddress || !depositAddress.trim()
                  ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white hover:scale-[1.02]'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Address
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Additional Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" />
            Security Tips
          </h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-blue-400">•</span>
              Always double-check the address before saving
            </li>
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-blue-400">•</span>
              Use a dedicated wallet for receiving deposits
            </li>
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-blue-400">•</span>
              Monitor the address regularly for incoming transactions
            </li>
          </ul>
        </div>

        <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-400" />
            Important Notes
          </h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-yellow-400">•</span>
              Changing the address affects all future deposits
            </li>
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-yellow-400">•</span>
              Existing deposit proofs will still use the old address
            </li>
            <li className="flex items-start gap-3 text-charcoal-gray">
              <span className="text-yellow-400">•</span>
              Inform users about the change if needed
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WalletSettings;